import {
  GameConfiguration,
  VinhosVariantId,
} from './types';
import { vinhosEditions, vinhosModules } from './metadata';

export const VINHOS_CONFIG_SCHEMA_VERSION = 2;

export const DEFAULT_VINHOS_CONFIGURATION: GameConfiguration = {
  schemaVersion: VINHOS_CONFIG_SCHEMA_VERSION,
  playerCount: 2,
  activeVariantId: 'special-vintage-2016',
  specialVintage2016: true,
  vintage2010: false,
  experts: false,
  islands: false,
  tastingRoom: false,
  connoisseur: false,
};

export type ConfigurationValidationResult = {
  valid: boolean;
  errors: string[];
};

function editionActivatable(editionId: string): boolean {
  return (
    vinhosEditions.find((edition) => edition.id === editionId)?.activatable ??
    false
  );
}

function moduleActivatable(moduleId: string): boolean {
  return (
    vinhosModules.find((module) => module.id === moduleId)?.activatable ?? false
  );
}

export function getActiveVariantId(config: GameConfiguration): VinhosVariantId {
  if (
    config.activeVariantId === 'reserve-2010' ||
    config.activeVariantId === 'special-vintage-2016'
  ) {
    return config.activeVariantId;
  }
  // Legacy configs without activeVariantId
  if (config.vintage2010) return 'reserve-2010';
  return 'special-vintage-2016';
}

export function syncVariantFlags(
  config: GameConfiguration
): GameConfiguration {
  const activeVariantId = getActiveVariantId(config);
  return {
    ...config,
    schemaVersion: VINHOS_CONFIG_SCHEMA_VERSION,
    activeVariantId,
    specialVintage2016: activeVariantId === 'special-vintage-2016',
    vintage2010: activeVariantId === 'reserve-2010',
  };
}

export function isModuleCompatibleWithVariant(
  moduleId: string,
  variantId: VinhosVariantId
): boolean {
  const module = vinhosModules.find((m) => m.id === moduleId);
  if (!module) return false;
  const entry = module.variantCompatibility.find((c) => c.variantId === variantId);
  return entry?.compatible ?? false;
}

export function validateConfiguration(
  config: GameConfiguration
): ConfigurationValidationResult {
  const errors: string[] = [];

  // Check raw flags before sync — contradictory legacy toggles are invalid input.
  if (config.specialVintage2016 && config.vintage2010) {
    errors.push(
      'Nie można jednocześnie wybrać Special Vintage 2016 i Reserve 2010.'
    );
  }

  const synced = syncVariantFlags(config);
  const variantId = synced.activeVariantId;

  if (!synced.specialVintage2016 && !synced.vintage2010) {
    errors.push('Wybierz co najmniej jeden wariant gry.');
  }

  if (
    variantId === 'reserve-2010' &&
    !editionActivatable('reserve-2010')
  ) {
    errors.push(
      'Reserve 2010 jest w przygotowaniu i nie może być aktywowany.'
    );
  }

  const moduleFlags: { key: keyof GameConfiguration; id: string }[] = [
    { key: 'experts', id: 'experts' },
    { key: 'islands', id: 'islands' },
    { key: 'tastingRoom', id: 'tasting-room' },
    { key: 'connoisseur', id: 'connoisseur' },
  ];

  for (const { key, id } of moduleFlags) {
    if (!synced[key]) continue;
    if (!moduleActivatable(id)) {
      errors.push(`Moduł ${id} nie może być aktywowany.`);
      continue;
    }
    if (!isModuleCompatibleWithVariant(id, variantId)) {
      errors.push(
        `Moduł ${id} nie jest kompletny dla wariantu ${variantId}.`
      );
    }
  }

  if (synced.playerCount < 1 || synced.playerCount > 4) {
    errors.push('Liczba graczy musi mieścić się w zakresie 1–4.');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

export function normalizeConfiguration(
  config: GameConfiguration
): GameConfiguration {
  let activeVariantId = getActiveVariantId(config);
  if (
    activeVariantId === 'reserve-2010' &&
    !editionActivatable('reserve-2010')
  ) {
    activeVariantId = 'special-vintage-2016';
  }

  const base = syncVariantFlags({
    ...config,
    activeVariantId,
  });

  const stripIfIncompatible = (enabled: boolean, moduleId: string) =>
    enabled &&
    moduleActivatable(moduleId) &&
    isModuleCompatibleWithVariant(moduleId, activeVariantId);

  return {
    ...base,
    experts: stripIfIncompatible(base.experts, 'experts'),
    islands: stripIfIncompatible(base.islands, 'islands'),
    tastingRoom: stripIfIncompatible(base.tastingRoom, 'tasting-room'),
    connoisseur: stripIfIncompatible(base.connoisseur, 'connoisseur'),
  };
}

export function toggleEdition(
  config: GameConfiguration,
  editionId: 'special-vintage-2016' | 'reserve-2010' | 'vintage-2010'
): GameConfiguration {
  const target: VinhosVariantId =
    editionId === 'special-vintage-2016'
      ? 'special-vintage-2016'
      : 'reserve-2010';

  if (target === 'reserve-2010' && !editionActivatable('reserve-2010')) {
    return normalizeConfiguration({
      ...config,
      activeVariantId: 'special-vintage-2016',
    });
  }

  return normalizeConfiguration({
    ...config,
    activeVariantId: target,
    specialVintage2016: target === 'special-vintage-2016',
    vintage2010: target === 'reserve-2010',
  });
}

export function toggleModule(
  config: GameConfiguration,
  moduleKey: keyof Pick<
    GameConfiguration,
    'experts' | 'islands' | 'tastingRoom' | 'connoisseur'
  >
): GameConfiguration {
  const moduleId =
    moduleKey === 'tastingRoom' ? 'tasting-room' : moduleKey;
  const variantId = getActiveVariantId(config);
  if (
    !moduleActivatable(moduleId) ||
    !isModuleCompatibleWithVariant(moduleId, variantId)
  ) {
    return { ...config, [moduleKey]: false };
  }
  return {
    ...config,
    [moduleKey]: !config[moduleKey],
  };
}

export function getActiveModuleIds(config: GameConfiguration): string[] {
  const modules: string[] = [];
  if (config.experts) modules.push('experts');
  if (config.islands) modules.push('islands');
  if (config.tastingRoom) modules.push('tasting-room');
  if (config.connoisseur) modules.push('connoisseur');
  return modules;
}

/** @deprecated Use getActiveVariantId */
export function getActiveEditionId(config: GameConfiguration): string {
  return getActiveVariantId(config);
}

export function configurationSummary(config: GameConfiguration): string {
  const edition =
    getActiveVariantId(config) === 'reserve-2010'
      ? 'Reserve 2010'
      : 'Special Vintage 2016';
  const modules = getActiveModuleIds(config);
  const modulesLabel =
    modules.length > 0 ? modules.join(', ') : 'bez dodatków';
  return `${config.playerCount} graczy · ${edition} · ${modulesLabel}`;
}

export function configurationWouldResetGuided(
  previous: GameConfiguration,
  next: GameConfiguration
): boolean {
  // Variant switch keeps independent progress — only module changes reset tutorials.
  if (getActiveVariantId(previous) !== getActiveVariantId(next)) {
    return false;
  }
  return (
    previous.experts !== next.experts ||
    previous.islands !== next.islands ||
    previous.tastingRoom !== next.tastingRoom ||
    previous.connoisseur !== next.connoisseur
  );
}

export function configurationWouldSwitchVariant(
  previous: GameConfiguration,
  next: GameConfiguration
): boolean {
  return getActiveVariantId(previous) !== getActiveVariantId(next);
}
